import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { containsTarget } from '../../lib/DomUtils/utils';
import { GlobalMenuState, useGlobalMenu } from '../../contexts/GlobalMenuStore';
import { KeysOfOpenPanelsType, MainNavigationProps, NavigationStateType } from './MainNavigationTypes';
import { KeyEvent } from '../../types/KeyEvent';
import { GlobalDocumentIds } from '../../types/GlobalDocumentIds';
import { ModalDialogBodyNoScrolling } from '../../components/organisms/ModalDialog/ModalDialog';
import { useAppContext } from '../../contexts/AppContext/AppContext';
import { Breakpoints } from '../../types/Breakpoints';

// duration of the sub panel container slide animation; must match the CSS transition/animation duration
// in _main-navigation.scss ($main-navigation-animation-duration)
export const SUB_PANEL_ANIMATION_DURATION = 250;
// class of the opened sub panel container; controls the slide-in/slide-out animation
const SUB_PANEL_OPENED_CLASS = 'main-navigation__sub-panels--opened';
// delay until the temporary slide class is removed, so the panels animate from their shifted positions
const SUB_PANEL_SLIDE_TRIGGER_DELAY = 20;

const getInitialState = (props: MainNavigationProps): NavigationStateType => {
  return new Map([
    [
      'mainNavigation',
      {
        panelData: props,
        panelName: undefined,
        panelLink: undefined,
        panelLevel: 0,
        loadingError: false,
        fetchUrl: undefined,
        nextPanelButtonRef: undefined,
      },
    ],
  ]);
};

export function useMainNavigation(props: MainNavigationProps) {
  const { closeMenus, currentOpenMenu } = useGlobalMenu();
  const { windowInnerWidth } = useAppContext();

  const mapRef = useRef<NavigationStateType>(getInitialState(props));
  const [keysOfOpenPanels, setKeysOfOpenPanels] = useState<KeysOfOpenPanelsType>(['mainNavigation']);
  const [subPanelContainerClass, setSubPanelContainerClass] = useState<string>('main-navigation__sub-panels');
  // temporary class for the slide-in/slide-out animation of further sub panels (3rd or more)
  const [subPanelsSlideClass, setSubPanelsSlideClass] = useState<string | undefined>(undefined);
  const [activeOpenButtonId, setActiveOpenButtonId] = useState<string | undefined>(undefined);
  const navRef = useRef<HTMLElement>(null);
  const subPanelsContainerRef = useRef<HTMLDivElement>(null);
  const mainNavigationStartingPoint = useRef<string>(undefined);
  const closeTimeoutRef = useRef<number | undefined>(undefined);
  // true while the closing animation of the sub panel container is running
  const isClosingRef = useRef(false);
  // current panel state for stable callbacks (closeAllSubPanels etc. must not change their identity per render)
  const keysOfOpenPanelsRef = useRef<KeysOfOpenPanelsType>(keysOfOpenPanels);

  useEffect(() => {
    keysOfOpenPanelsRef.current = keysOfOpenPanels;
  }, [keysOfOpenPanels]);

  /**
   * If sub panels open, content will be hidden (attribute 'inert'), body will be not scrollable.
   */
  const hideBackgroundContent = () => {
    const mainContent = document.getElementById(GlobalDocumentIds.mainContentWrapper);
    const palContainer = document.getElementById(GlobalDocumentIds.palContainer);
    document.body.classList.add(ModalDialogBodyNoScrolling);
    mainContent?.setAttribute('inert', 'true');
    palContainer?.setAttribute('inert', 'true');
  };

  /**
   * If there is no sub panels open, content will be interactive again
   */
  const showBackgroundContent = () => {
    const mainContent = document.getElementById(GlobalDocumentIds.mainContentWrapper);
    const palContainer = document.getElementById(GlobalDocumentIds.palContainer);
    document.body.classList.remove(ModalDialogBodyNoScrolling);
    mainContent?.removeAttribute('inert');
    palContainer?.removeAttribute('inert');
  };

  /**
   * Hiding or showing content depends on count of open sub panels. Be aware first panel is main panel.
   * Changing classes on container panel for CSS transitions (max 2 sub panels ar visible).
   * If all sub panels are closed, activeOpenButtonId of this panel will be set to undefined,
   * so it is possible to close NextPanelButton without losing focus.
   */

  useEffect(() => {
    if (mainNavigationStartingPoint.current) {
      // Restore focus only if the main navigation is still visible:
      // - the menu is explicitly open (mobile/tablet) or
      // - the viewport is wide enough that the navigation is permanently visible (desktop).
      // On desktop the global menu state is always NONE (HeaderCollapsible auto-closes the menu at
      // the desktop breakpoint), so the currentOpenMenu check alone would never pass there.
      if (currentOpenMenu === GlobalMenuState.MAIN_NAVIGATION || windowInnerWidth >= Breakpoints.l_screen) {
        document.getElementById(mainNavigationStartingPoint.current)?.focus();
      }
      mainNavigationStartingPoint.current = undefined;
    }

    if (activeOpenButtonId && keysOfOpenPanels.length === 1) {
      mainNavigationStartingPoint.current = activeOpenButtonId;
      setActiveOpenButtonId(undefined);
      showBackgroundContent();
      setSubPanelContainerClass('main-navigation__sub-panels');
    }

    if (keysOfOpenPanels.length > 1) {
      const level = keysOfOpenPanels.length >= 4 ? 3 : keysOfOpenPanels.length - 1;
      hideBackgroundContent();
      // during a pending close animation the target panel level must not be overwritten
      // (e.g. by a late data arrival of the closing panel)
      if (!closeTimeoutRef.current) {
        setSubPanelContainerClass(currentClass => {
          const baseClass = `main-navigation__sub-panels main-navigation__sub-panels--subpanel${level}-visible`;
          // keep the opened state, if it has already been reached
          return currentClass.includes(SUB_PANEL_OPENED_CLASS) ? `${baseClass} ${SUB_PANEL_OPENED_CLASS}` : baseClass;
        });
      }
    }
  }, [keysOfOpenPanels, activeOpenButtonId, windowInnerWidth]);

  /**
   * The sub panel container slides in only when the content of the last open sub panel is loaded.
   * If the panel data is already cached (or a loading error occurred), the container is opened as well.
   * While a close animation is running, the container stays closed.
   */
  useEffect(() => {
    if (!isClosingRef.current && keysOfOpenPanels.length > 1) {
      const lastPanel = mapRef.current.get(keysOfOpenPanels[keysOfOpenPanels.length - 1]);
      if (lastPanel?.panelData || lastPanel?.loadingError) {
        setSubPanelContainerClass(currentClass =>
          currentClass.includes(SUB_PANEL_OPENED_CLASS) ? currentClass : `${currentClass} ${SUB_PANEL_OPENED_CLASS}`
        );
      }
    }
  }, [keysOfOpenPanels]);

  /**
   * The temporary slide class is removed after the slide animation has finished,
   * otherwise the animation would be cancelled and the panels would jump back.
   */
  useEffect(() => {
    if (subPanelsSlideClass) {
      const timeout = window.setTimeout(() => {
        setSubPanelsSlideClass(undefined);
      }, SUB_PANEL_ANIMATION_DURATION + SUB_PANEL_SLIDE_TRIGGER_DELAY);
      return () => {
        window.clearTimeout(timeout);
      };
    }
    return undefined;
  }, [subPanelsSlideClass]);

  /**
   * Showing content depends on count of open sub panels. Be aware first panel is main panel.
   */
  useEffect(() => {
    return () => {
      showBackgroundContent();
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Starts the closing animation of the sub panel container.
   * Without targetLevel: the container slides out to the left (under the main panel),
   * the panels are removed from the navigation state only after the animation has finished.
   * With targetLevel: a sub panel remains open, the container slides back to the given panel level
   * (left/width transition) and the closed panels are removed only after the animation.
   * @applyClose: removes the closed panels from the navigation state
   * @targetLevel: target panel level of the remaining sub panels (keeps the container opened)
   */
  const closeSubPanelsWithAnimation = useCallback(
    (applyClose: () => void, targetLevel: number | undefined = undefined): void => {
      const currentKeysOfOpenPanels = keysOfOpenPanelsRef.current;
      if (currentKeysOfOpenPanels.length > 1) {
        // subpanel3-visible is the class for 3 or more open sub panels
        const level = Math.min(
          targetLevel ?? (currentKeysOfOpenPanels.length >= 4 ? 3 : currentKeysOfOpenPanels.length - 1),
          3
        );
        const openedClass = targetLevel !== undefined ? ` ${SUB_PANEL_OPENED_CLASS}` : '';
        if (targetLevel === undefined || targetLevel < 2) {
          isClosingRef.current = true;
          closeTimeoutRef.current = window.setTimeout(() => {
            closeTimeoutRef.current = undefined;
            isClosingRef.current = false;
            applyClose();
          }, SUB_PANEL_ANIMATION_DURATION);
        } else {
          // when at least two sub panels remain, the closed panels are removed immediately:
          // the slide-out animation still shifts the remaining panels to their final positions,
          // so the flex layout change does not cause a visible jump
          applyClose();
        }
        // when at least two sub panels remain, the panels slide back to the right (from the 3rd panel onwards)
        if (targetLevel !== undefined && targetLevel >= 2) {
          setSubPanelsSlideClass('main-navigation__sub-panels--sliding-out');
        }
        setSubPanelContainerClass(
          `main-navigation__sub-panels main-navigation__sub-panels--subpanel${level}-visible${openedClass}`
        );
      }
    },
    []
  );

  const closeAllSubPanels = useCallback(() => {
    if (keysOfOpenPanelsRef.current.length > 1) {
      closeSubPanelsWithAnimation(() => {
        mapRef.current = getInitialState(props);
        setKeysOfOpenPanels(['mainNavigation']);
      });
      return;
    }
    mapRef.current = getInitialState(props);
    setKeysOfOpenPanels(currentKeysOfOpenPanels =>
      currentKeysOfOpenPanels.length === 1 ? currentKeysOfOpenPanels : ['mainNavigation']
    );
  }, [closeSubPanelsWithAnimation, props]);

  /**
   * Close the nav when the focus leaves it.
   * */
  const closeMainNavigationWhenFocusOut = (event: React.FocusEvent) => {
    const nextElement = event.relatedTarget;
    // We need to check if our next target is the Main Menu button.
    // Otherwise, after navigating to submenus, this will trigger closeMenus() ADDITIONALLY to the button onClick(),
    // which leads to the Menu not closing correctly on button click due to a state mismatch in GlobalMenuStore.
    const isMenuButton = nextElement?.classList.contains('header-collapsible--navigation');
    if (navRef.current && nextElement && !isMenuButton && !containsTarget(navRef, nextElement)) {
      closeAllSubPanels();
      closeMenus();
    }
  };

  const clickOutsideMainNavigationHandler = useCallback(
    (event: MouseEvent) => {
      if (!navRef.current?.contains(event.target as Node)) {
        closeAllSubPanels();

        if (currentOpenMenu === GlobalMenuState.MAIN_NAVIGATION) {
          closeMenus();
        }
      }
    },
    [closeAllSubPanels, closeMenus, currentOpenMenu]
  );

  useEffect(() => {
    document.addEventListener('click', clickOutsideMainNavigationHandler);

    return () => {
      if (clickOutsideMainNavigationHandler) {
        document.removeEventListener('click', clickOutsideMainNavigationHandler);
      }
    };
  }, [clickOutsideMainNavigationHandler, closeAllSubPanels, closeMenus, currentOpenMenu, keysOfOpenPanels]);

  const closeSubPanelsOnEscape = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.code === KeyEvent.Escape) {
      closeAllSubPanels();
    }
  };

  /**
   * If main navigation is closed via GlobalMenuStore (burger button), close sub panels as well
   */
  useEffect(() => {
    if (currentOpenMenu !== GlobalMenuState.MAIN_NAVIGATION) {
      closeAllSubPanels();
    }
  }, [closeAllSubPanels, currentOpenMenu]);

  /**
   * Triggers axios call to fetch sub panel's data and saves it in navigation state.
   * @panelData: sub panel's data
   * @panelName: name of sub panel
   * @panelLink: link to the page with the navigation of sub panel
   * @panelLevel: current panel number, used to define from which sub panel comes requests
   * @loadingError: to show axios call errors
   * @fetchUrl: used to refetch data, if fetching error occurs
   * @openButtonRef: used to focus correct button, if sub panel closes
   * */
  const fetchSubPanel = (
    fetchUrl: string,
    panelLevel: number,
    name: string,
    url: string,
    nextPanelButtonRef: React.MutableRefObject<HTMLButtonElement | null>,
    loadingError: boolean = false
  ): void => {
    // a new open request cancels a pending close animation
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = undefined;
      isClosingRef.current = false;
    }
    const newPanel = {
      panelData: undefined,
      panelName: name,
      panelLink: url,
      panelLevel,
      loadingError: false,
      fetchUrl,
      nextPanelButtonRef,
    };
    // if request comes from one panel before, we replace last panel with new one in the list
    if (keysOfOpenPanels.length > 1 && keysOfOpenPanels.length - 1 === panelLevel) {
      // caching: if request comes from same button, it will be skipped
      const panel = mapRef.current.get(fetchUrl);
      if (panel?.panelLevel === panelLevel - 1 && !loadingError) {
        return;
      }
      keysOfOpenPanels.pop();
      keysOfOpenPanels.push(fetchUrl);
    }
    // if request comes from last panel we add new panel at the end of list
    if (keysOfOpenPanels.length === 1 || (keysOfOpenPanels.length > 1 && keysOfOpenPanels.length === panelLevel)) {
      keysOfOpenPanels.push(fetchUrl);
    }
    setKeysOfOpenPanels([...keysOfOpenPanels]);
    // reset scroll position of the sub panels container, so slide animations are not shifted
    if (subPanelsContainerRef.current) {
      subPanelsContainerRef.current.scrollLeft = 0;
    }
    // when a further sub panel (3rd or more) is opened, the visible panels slide in from the right
    if (keysOfOpenPanels.length > 3) {
      setSubPanelsSlideClass('main-navigation__sub-panels--sliding-in');
    }
    // if panel already exist and there was no loadingError, then skip axios call
    const panel = mapRef.current.get(fetchUrl);
    if (panel && !panel.loadingError) {
      return;
    }
    if (!panel || (panel && panel.loadingError)) {
      mapRef.current.set(fetchUrl, newPanel);
    }
    axios
      .get(fetchUrl)
      .then(res => {
        const oldPanel = mapRef.current.get(fetchUrl);
        if (oldPanel) {
          mapRef.current.set(fetchUrl, { ...oldPanel, panelData: res.data });
          setKeysOfOpenPanels([...keysOfOpenPanels]);
        }
      })
      .catch(() => {
        const oldPanel = mapRef.current.get(fetchUrl);
        if (oldPanel) {
          mapRef.current.set(fetchUrl, { ...oldPanel, loadingError: true });
          setKeysOfOpenPanels([...keysOfOpenPanels]);
        }
      });
  };

  /**
   * There are two cases for back buttons in sub panels:
   * 1) Request comes from last panel: only last panel will be closed.
   * 2) Request comes one panel before: last two panels will be closed
   * */
  const closeSubPanel = (panelLevel: number): void => {
    const removeLastTwoPanels = () => {
      const newKeysOfOpenPanels = [...keysOfOpenPanels];
      newKeysOfOpenPanels.pop();
      newKeysOfOpenPanels.pop();
      setKeysOfOpenPanels([...newKeysOfOpenPanels]);
    };
    const removeLastPanel = () => {
      const newKeysOfOpenPanels = [...keysOfOpenPanels];
      newKeysOfOpenPanels.pop();
      setKeysOfOpenPanels([...newKeysOfOpenPanels]);
    };

    // if request comes from one panel before we have to remove last two panels of the list
    if (keysOfOpenPanels.length > 2 && keysOfOpenPanels.length - 1 > panelLevel) {
      // if only the main navigation panel remains, animate the container out before removing the panels
      if (keysOfOpenPanels.length - 2 === 1) {
        closeSubPanelsWithAnimation(removeLastTwoPanels);
        return;
      }
      // if at least 2 sub panels remain, the visible panels slide out to the right
      // before the closed panels are removed
      if (keysOfOpenPanels.length - 3 > 1) {
        closeSubPanelsWithAnimation(removeLastTwoPanels, keysOfOpenPanels.length - 3);
        return;
      }
      removeLastTwoPanels();
    }
    // if request comes from last panel we have to remove only last panel of the list
    if (keysOfOpenPanels.length > 1 && keysOfOpenPanels.length - 1 === panelLevel) {
      // if only the main navigation panel remains, animate the container out before removing the panels
      if (keysOfOpenPanels.length - 1 === 1) {
        closeSubPanelsWithAnimation(removeLastPanel);
        return;
      }
      // a sub panel remains open: the container slides back (left/width transition),
      // the closed panel is removed only after the animation
      closeSubPanelsWithAnimation(removeLastPanel, keysOfOpenPanels.length - 2);
    }
  };

  return {
    navRef,
    subPanelsContainerRef,
    activeOpenButtonId,
    subPanelContainerClass,
    subPanelsSlideClass,
    setActiveOpenButtonId,
    closeMainNavigationWhenFocusOut,
    closeAllSubPanels,
    closeSubPanelsOnEscape,
    fetchSubPanel,
    closeSubPanel,
    keysOfOpenPanels,
    mapRef,
  };
}
